import { useTranslation } from 'react-i18next';
import { useBeforeUnload, useEffectOnce } from 'react-use';
import {
  CustomerCreateInput,
  CustomerUpdateInput,
  useCreateCustomerMutation,
  useCreateVendorMutation,
  useUpdateCustomerMutation,
  useUpdateVendorMutation,
  VendorCreateInput,
  VendorUpdateInput,
} from '../../../../api/gql';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { useXmlDataStore } from '../../../../hooks/useXmlDataStore';
import { useXmlProcessController } from '../../../../hooks/useXmlProcessController';
import { notify } from '../../../../utils/notification';
import { isCreateCustomerResponse, isCreateVendorResponse } from '../../../../utils/typeGuards';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';

const TRANS_NS = 'xml-upload';

export const XmlUpsertContacts = () => {
  const { t } = useTranslation(TRANS_NS);
  const { xmlDataModel } = useXmlDataStore();
  const { updateXmlProcess } = useXmlProcessController();
  const { safeNavigate } = useSafeNavigation();

  const { mutateAsync: createCustomerAsync } = useCreateCustomerMutation();
  const { mutateAsync: updateCustomerAsync } = useUpdateCustomerMutation();
  const { mutateAsync: createVendorAsync } = useCreateVendorMutation();
  const { mutateAsync: updateVendorAsync } = useUpdateVendorMutation();

  // ask user to confirm before leaving page since the data are not saved
  useBeforeUnload(true, 'Opuštěním stránky přijdete o data v paměti a proces nahrání xml budete muset začít znovu.');

  const upsertCustomers = async () => {
    const res = await Promise.all(
      xmlDataModel.xmlCustomers
        .filter((c) => c.shouldBeUpserted && !c.isUpserted)
        .map(async (customer) => {
          // create customer on the server
          const customerPayload: CustomerCreateInput | CustomerUpdateInput = {
            id: customer.details.id,
            name: customer.details.name ?? '',
            vatId: customer.details.vatId,
            taxId: customer.details.taxId,
            address: customer.details.address,
            email: customer.details.email,
            phoneNumber: customer.details.phoneNumber?.toString(),
            remindingTone: customer.details.remindingTone,
          };

          try {
            let res;
            if (customer.details.id) {
              //customer exists, update it
              res = await updateCustomerAsync({
                customer: customerPayload as CustomerUpdateInput,
              });
            } else {
              //customer does not yet exist, create it
              res = await createCustomerAsync({
                customer: customerPayload,
              });
              xmlDataModel.updateCustomer(customer.id, {
                id: res.createCustomer.id,
              });
            }
            xmlDataModel.setContactUpserted(customer.id);
            return res;
          } catch (e) {
            console.error(e);
            notify.negative({
              message: t('upsertContacts.customerWasNotUpdated', {
                name: customer.details.name,
              }),
            });
          }
        }),
    );

    //return true if every customer was updated
    return res.every((r) => r && (isCreateCustomerResponse(r) ? r.createCustomer.name : r.updateCustomer.name));
  };

  const upsertVendors = async () => {
    const res = await Promise.all(
      xmlDataModel.xmlVendors
        .filter((v) => v.shouldBeUpserted && !v.isUpserted)
        .map(async (vendor) => {
          let res;
          // create customer on the server
          const vendorPayload: VendorCreateInput | VendorUpdateInput = {
            id: vendor.details.id,
            name: vendor.details.name ?? '',
            vatId: vendor.details.vatId,
            taxId: vendor.details.taxId,
            address: vendor.details.address,
          };
          try {
            if (vendor.details.id) {
              //vendor exists, update it
              res = await updateVendorAsync({
                vendor: vendorPayload as VendorUpdateInput,
              });
            } else {
              // vendor does not yet exist, create it
              res = await createVendorAsync({
                vendor: vendorPayload,
              });
              xmlDataModel.updateVendor(vendor.id, { id: res.createVendor.id });
            }

            xmlDataModel.setContactUpserted(vendor.id);
            return res;
          } catch (e) {
            console.error(e);
            notify.negative({
              message: t('upsertContacts.vendorWasNotUpdated', {
                name: vendor.details.name,
              }),
            });
          }
        }),
    );
    //return true if every vendor was updated
    return res.every((r) => r && (isCreateVendorResponse(r) ? r.createVendor.name : r.updateVendor.name));
  };

  const upsertContacts = async () => {
    const customersUpsertRes = await upsertCustomers();
    const vendorsUpsertRes = await upsertVendors();

    if (customersUpsertRes && vendorsUpsertRes) {
      notify.positive({ message: t('upsertContacts.allCustomersWereUpdated') });
      updateXmlProcess();
      return;
    }

    safeNavigate('/upload/xml/summary');
  };

  useEffectOnce(() => {
    upsertContacts();
  });

  return <FullSizeLoader />;
};
